'use client';

/**
 * @packageDocumentation
 * @category Component
 */

import 'BomdisoftComponents/inputs/segmented/styles.css';
import 'BomdisoftComponents/inputs/toggle-group/styles.css';
import { joinClasses } from 'BomdisoftComponents/html/index';
import { resolveExclusiveValue, type ToggleGroupActiveVariant, type ToggleGroupOverflow, type ToggleGroupShape, type ToggleGroupSize } from 'BomdisoftComponents/inputs/toggle-group/render';
import { TabOverflowArrows, useTabOverflow } from 'BomdisoftComponents/tab/overflow';
import { type ComponentProps, createContext, type ReactNode, useContext } from 'react';

export { resolveExclusiveValue, type ToggleGroupActiveVariant, type ToggleGroupOverflow, type ToggleGroupShape, type ToggleGroupSize } from 'BomdisoftComponents/inputs/toggle-group/render';

type ToggleGroupContextValue = {
  activeVariant: ToggleGroupActiveVariant;
  onSelect: (value: string) => void;
  selected: string | null;
  shape: ToggleGroupShape;
  size: ToggleGroupSize;
};

const ToggleGroupContext = createContext<ToggleGroupContextValue | null>(null);

export type ToggleGroupProps = Omit<ComponentProps<'fieldset'>, 'onChange'> & {
  activeVariant?: ToggleGroupActiveVariant;
  /** The name of the choice, drawn above the control the way `TextField` and
   * `Select` draw theirs. A group in a form reads as an unlabelled row of
   * buttons without it; a group in a toolbar, where the surrounding controls
   * carry no labels either, passes `aria-label` alone and omits this. */
  label?: ReactNode;
  onChange: (value: string | null) => void;
  overflow?: ToggleGroupOverflow;
  shape?: ToggleGroupShape;
  size?: ToggleGroupSize;
  value: string | null;
};

export const ToggleGroup = ({ activeVariant = 'primary', children, className, label, onChange, overflow = 'wrap', shape = 'joined', size = 'medium', value, ...rest }: ToggleGroupProps) => {
  const scrolling = overflow === 'scroll';
  /* A segment is pressed, not selected, so the hook is told what to look for:
     its tab selector matches none of these buttons and would reveal nothing. */
  const { frameProps, page, rowProps, scrollerClassName } = useTabOverflow({ selected: scrolling ? value : undefined, selectedSelector: '[aria-pressed="true"]' });
  const groupClassName = joinClasses('bomdisoft-segmented', shape === 'pill' && 'bomdisoft-toggle-group-pill', scrolling && scrollerClassName, label ? undefined : className);
  const segments = <ToggleGroupContext.Provider value={{ activeVariant, onSelect: next => onChange(resolveExclusiveValue(value, next)), selected: value, shape, size }}>{children}</ToggleGroupContext.Provider>;
  /* Unlabelled, the fieldset IS the bordered control, which is what every
     toolbar consumer styles through `className`. A legend drawn inside that
     border would sit in the middle of the segments, so a labelled group nests:
     the fieldset becomes the field, and the control moves one level in. */
  /* The scrolling box and the box the chevrons are positioned in cannot be the
     same element: absolutely positioned children of a scroller scroll away with
     the content. So a scrolling group nests one level, the outer box holding the
     chevrons still and the inner one carrying the border and the segments.
     Wrapping only happens while scrolling, so the default renders exactly the
     markup it rendered before this prop existed. */
  const control = (
    <div {...(scrolling ? rowProps : {})} className={groupClassName} data-overflow={overflow} data-shape={shape} data-tone={activeVariant}>
      {segments}
    </div>
  );
  if (!label) {
    if (!scrolling) {
      return (
        <fieldset {...rest} className={groupClassName} data-overflow={overflow} data-shape={shape} data-tone={activeVariant}>
          {segments}
        </fieldset>
      );
    }
    return (
      <fieldset {...rest} {...frameProps} className={joinClasses(frameProps.className, 'bomdisoft-toggle-group-scroll-frame', className)}>
        {control}
        <TabOverflowArrows onPage={page} />
      </fieldset>
    );
  }
  return (
    <fieldset {...rest} className={joinClasses('bomdisoft-toggle-group-field', className)}>
      <legend className='bomdisoft-toggle-group-legend'>{label}</legend>
      {scrolling ? (
        <div {...frameProps}>
          {control}
          <TabOverflowArrows onPage={page} />
        </div>
      ) : (
        control
      )}
    </fieldset>
  );
};

export type ToggleButtonProps = Omit<ComponentProps<'button'>, 'value'> & {
  /** Overrides the group's activeVariant for this button only — e.g. a
   * "Current"/"Deleted" pair where each side has its own meaning regardless of
   * what the rest of the group uses. */
  activeVariant?: ToggleGroupActiveVariant;
  /** A second line under the name, for a choice whose consequence the name
   * cannot carry on its own: `json` means nothing until something says
   * "payload variants". The segment stacks and takes the group's full width
   * when any of its buttons is described, so the row still reads as one
   * control rather than a run of cards. */
  description?: ReactNode;
  value: string;
};

export const ToggleButton = ({ activeVariant, children, className, description, onClick, value, ...rest }: ToggleButtonProps) => {
  const group = useContext(ToggleGroupContext);
  const pressed = group ? group.selected === value : false;
  const shape = group?.shape ?? 'joined';
  const buttonClassName = joinClasses('bomdisoft-segmented-segment', shape === 'pill' && 'bomdisoft-toggle-group-pill-segment', description !== undefined && 'bomdisoft-toggle-group-described', className);
  return (
    <button
      {...rest}
      aria-pressed={pressed}
      className={buttonClassName}
      data-selected={pressed ? '' : undefined}
      data-shape={shape}
      data-size={group?.size ?? 'medium'}
      data-tone={activeVariant}
      onClick={event => {
        onClick?.(event);
        group?.onSelect(value);
      }}
      type='button'
      value={value}
    >
      {description === undefined ? children : <span className='bomdisoft-toggle-group-name'>{children}</span>}
      {description === undefined ? null : <span className='bomdisoft-toggle-group-description'>{description}</span>}
    </button>
  );
};
